import React, { useState, useEffect } from 'react';
import { View, Text, TextInput, Pressable, StyleSheet, Platform, ActivityIndicator, Alert } from 'react-native';
import { User, Home as HomeIcon, LogOut } from '../lib/icons';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import WebScrollView from '../components/WebScrollView';
import HomePicker from '../components/HomePicker';
import { shadows } from '../lib/styles';
import { acceptHomeInvitation, createHomeInvitation, removeHomeMember, revokeHomeInvitation, transferHomeOwnership } from '../lib/household';
import { useActiveHome } from '../contexts/ActiveHomeContext';
import { openExternalLink } from '../lib/platform';

interface ProfileProps {
  session: any;
}

interface Profile {
  full_name: string;
  phone_number: string;
  created_at: string;
  terms_accepted_at: string;
}

interface HouseholdMember {
  id: string;
  user_id: string;
  role: 'owner' | 'co_owner' | 'member';
  created_at: string;
  profile_name: string;
}

interface HouseholdInvitation {
  id: string;
  email: string;
  role: 'co_owner' | 'member';
  status: string;
  expires_at: string;
}

type BillingProduct = {
  id: string;
  slug: string;
  name: string;
  description: string | null;
  kind: 'base_plan' | 'addon';
  billing_mode: 'subscription' | 'payment';
  stripe_price_id: string | null;
  plan_value: string | null;
  amount_cents: number;
  currency: string;
  interval: string | null;
  included_units: number | null;
  additional_unit_amount_cents: number | null;
  metadata: Record<string, any> | null;
};

type BillingOverview = {
  customer: {
    stripe_customer_id: string | null;
  };
  home: {
    id: string;
    address: string;
    market_id: string | null;
    market_name: string | null;
    plan: string;
    plan_source: string;
    plan_payer_id: string | null;
    is_primary_owner: boolean;
  } | null;
  current_plan: {
    value: string;
    label: string;
    source: 'stripe' | 'complimentary' | 'manual' | 'none';
    home_plan: string;
    subscription: {
      id: string;
      status: string;
      current_period_end: string | null;
      cancel_at_period_end: boolean;
    } | null;
  };
  products: BillingProduct[];
  checkout_allowed: boolean;
};

const Profile: React.FC<ProfileProps> = ({ session }) => {
  const { homes, activeHome, setActiveHomeById } = useActiveHome();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    fullName: '',
    phoneNumber: ''
  });
  const [emailInput, setEmailInput] = useState(session?.user?.email || '');
  const [emailSaving, setEmailSaving] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [emailSuccess, setEmailSuccess] = useState('');
  const [householdLoading, setHouseholdLoading] = useState(false);
  const [householdError, setHouseholdError] = useState('');
  const [acceptingInvite, setAcceptingInvite] = useState(false);
  const [homeId, setHomeId] = useState<string | null>(null);
  const [homeAddress, setHomeAddress] = useState<string>('');
  const [isPrimaryOwner, setIsPrimaryOwner] = useState(false);
  const [members, setMembers] = useState<HouseholdMember[]>([]);
  const [invitations, setInvitations] = useState<HouseholdInvitation[]>([]);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<'co_owner' | 'member'>('co_owner');
  const [inviteSubmitting, setInviteSubmitting] = useState(false);
  const [selectedTransferUserId, setSelectedTransferUserId] = useState<string | null>(null);
  const [transferringOwner, setTransferringOwner] = useState(false);
  const [billingOverview, setBillingOverview] = useState<BillingOverview | null>(null);
  const [billingLoading, setBillingLoading] = useState(false);
  const [billingError, setBillingError] = useState('');
  const [billingSuccess, setBillingSuccess] = useState('');
  const [checkoutProductId, setCheckoutProductId] = useState<string | null>(null);
  const [selectedBillingHomeId, setSelectedBillingHomeId] = useState<string | null>(null);

  useEffect(() => {
    if (homes.length === 0) {
      setSelectedBillingHomeId(null);
      return;
    }

    if (activeHome?.id && homes.some((home) => home.id === activeHome.id)) {
      setSelectedBillingHomeId(activeHome.id);
      return;
    }

    setSelectedBillingHomeId((current) =>
      current && homes.some((home) => home.id === current) ? current : homes[0].id,
    );
  }, [homes, activeHome?.id]);

  useEffect(() => {
    fetchProfile();
    fetchHouseholdData();
  }, [session, activeHome?.id]);

  useEffect(() => {
    fetchBillingOverview();
  }, [session?.user?.id, selectedBillingHomeId]);

  useEffect(() => {
    setEmailInput(session?.user?.email || '');
  }, [session?.user?.email]);

  useEffect(() => {
    const inviteToken = searchParams.get('invite');
    if (!inviteToken || !session?.user?.id) return;

    const acceptInvite = async () => {
      setAcceptingInvite(true);
      setHouseholdError('');
      try {
        await acceptHomeInvitation(inviteToken);
        await fetchHouseholdData();
        navigate('/profile', { replace: true });
      } catch (acceptError: any) {
        setHouseholdError(acceptError.message || 'Unable to accept household invite');
      } finally {
        setAcceptingInvite(false);
      }
    };

    acceptInvite();
  }, [searchParams, session?.user?.id]);

  useEffect(() => {
    const billingStatus = searchParams.get('billing');
    if (billingStatus === 'success') {
      setBillingSuccess('Checkout complete. Your billing status may take a moment to update.');
      fetchBillingOverview();
      navigate('/profile', { replace: true });
    } else if (billingStatus === 'cancelled') {
      setBillingError('Checkout was cancelled.');
      navigate('/profile', { replace: true });
    }
  }, [searchParams]);

  const fetchProfile = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('profiles')
        .select('full_name, phone_number, created_at, terms_accepted_at')
        .eq('id', session.user.id)
        .single();

      if (error) throw error;

      setProfile(data);
      setFormData({
        fullName: data.full_name || '',
        phoneNumber: data.phone_number || ''
      });
    } catch (error) {
      console.error('Error fetching profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    setSaving(true);
    setError('');

    try {
      const { error } = await supabase
        .from('profiles')
        .update({
          full_name: formData.fullName,
          phone_number: formData.phoneNumber
        })
        .eq('id', session.user.id);

      if (error) throw error;
      fetchProfile();
    } catch (error: any) {
      setError(error.message);
    } finally {
      setSaving(false);
    }
  };

  const fetchHouseholdData = async () => {
    if (!session?.user?.id) return;
    try {
      setHouseholdLoading(true);
      setHouseholdError('');

      const { data: membershipRows, error: membershipError } = await supabase
        .from('home_members')
        .select(`
          id,
          home_id,
          role,
          homes!inner (
            id,
            owner_id,
            address
          )
        `)
        .eq('user_id', session.user.id)
        .is('removed_at', null)
        .order('created_at', { ascending: true });

      if (membershipError) throw membershipError;
      if (!membershipRows || membershipRows.length === 0) {
        setHomeId(null);
        setHomeAddress('');
        setIsPrimaryOwner(false);
        setMembers([]);
        setInvitations([]);
        return;
      }

      const activeMembership = ((activeHome
        ? membershipRows.find((membership: any) => membership.home_id === activeHome.id)
        : null) || membershipRows[0]) as any;
      const membershipHome = Array.isArray(activeMembership.homes)
        ? activeMembership.homes[0]
        : activeMembership.homes;

      setHomeId(activeMembership.home_id);
      setHomeAddress(membershipHome?.address || '');
      setIsPrimaryOwner(activeMembership.role === 'owner' && membershipHome?.owner_id === session.user.id);

      const { data: memberRows, error: memberRowsError } = await supabase
        .from('home_members')
        .select('id, user_id, role, created_at')
        .eq('home_id', activeMembership.home_id)
        .is('removed_at', null)
        .order('created_at', { ascending: true });

      if (memberRowsError) throw memberRowsError;

      const profileIds = (memberRows || []).map((member) => member.user_id);
      const { data: profileRows, error: profileRowsError } = await supabase
        .from('profiles')
        .select('id, full_name')
        .in('id', profileIds);

      if (profileRowsError) throw profileRowsError;

      const profileNameById = new Map((profileRows || []).map((profileRow) => [profileRow.id, profileRow.full_name || 'Household user']));
      setMembers(
        (memberRows || []).map((memberRow) => ({
          ...memberRow,
          profile_name: profileNameById.get(memberRow.user_id) || 'Household user',
        })) as HouseholdMember[]
      );

      const { data: invitationRows, error: invitationRowsError } = await supabase
        .from('home_invitations')
        .select('id, email, role, status, expires_at')
        .eq('home_id', activeMembership.home_id)
        .eq('status', 'pending')
        .order('created_at', { ascending: false });

      if (!invitationRowsError) {
        setInvitations((invitationRows || []) as HouseholdInvitation[]);
      } else {
        setInvitations([]);
      }
    } catch (householdFetchError: any) {
      console.error('Error fetching household data:', householdFetchError);
      setHouseholdError(householdFetchError.message || 'Failed to load household data');
    } finally {
      setHouseholdLoading(false);
    }
  };

  const getFunctionErrorMessage = async (error: any, fallback: string) => {
    if (!error) return fallback;
    if (error.name === 'FunctionsHttpError' && error.context) {
      try {
        const payload = await error.context.json();
        if (typeof payload?.error === 'string' && payload.error.trim()) {
          return payload.error;
        }
      } catch {
        // Fall back to the generic function error below.
      }
    }
    return error.message || fallback;
  };

  const fetchBillingOverview = async () => {
    if (!session?.user?.id) return;
    try {
      setBillingLoading(true);
      setBillingError('');
      const { data, error: overviewError } = await supabase.functions.invoke('get-billing-overview', {
        body: {
          homeId: selectedBillingHomeId,
        },
      });

      if (overviewError) {
        throw new Error(await getFunctionErrorMessage(overviewError, 'Failed to load billing information'));
      }
      if (data?.error) throw new Error(data.error);
      setBillingOverview(data as BillingOverview);
    } catch (overviewError: any) {
      console.error('Error loading billing overview:', overviewError);
      setBillingError(overviewError.message || 'Failed to load billing information');
    } finally {
      setBillingLoading(false);
    }
  };

  const handleStartCheckout = async (product: BillingProduct) => {
    if (!billingOverview?.home?.id) {
      setBillingError('Select a home before starting checkout.');
      return;
    }
    if (!billingOverview.checkout_allowed) {
      setBillingError('Only the primary owner can manage billing for this home.');
      return;
    }
    if (!product.stripe_price_id) {
      setBillingError('This product is not ready for checkout yet.');
      return;
    }

    try {
      setCheckoutProductId(product.id);
      setBillingError('');
      setBillingSuccess('');
      const { data, error: checkoutError } = await supabase.functions.invoke('create-billing-checkout-session', {
        body: {
          homeId: billingOverview.home.id,
          productId: product.id,
          quantity: 1,
        },
      });

      if (checkoutError) {
        throw new Error(await getFunctionErrorMessage(checkoutError, 'Failed to start checkout'));
      }
      if (data?.error) throw new Error(data.error);
      if (!data?.url) throw new Error('Stripe did not return a checkout URL');

      await openExternalLink(data.url, { newTab: false, inAppBrowser: true });
    } catch (checkoutError: any) {
      console.error('Error starting billing checkout:', checkoutError);
      setBillingError(checkoutError.message || 'Failed to start checkout');
    } finally {
      setCheckoutProductId(null);
    }
  };

  const handleSendInvite = async () => {
    if (!homeId || !inviteEmail.trim()) return;
    try {
      setInviteSubmitting(true);
      setHouseholdError('');
      await createHomeInvitation({
        homeId,
        email: inviteEmail.trim(),
        role: inviteRole,
      });
      setInviteEmail('');
      await fetchHouseholdData();
    } catch (inviteError: any) {
      setHouseholdError(inviteError.message || 'Failed to send invite');
    } finally {
      setInviteSubmitting(false);
    }
  };

  const handleRevokeInvite = async (invitationId: string) => {
    if (!isPrimaryOwner) return;
    try {
      await revokeHomeInvitation(invitationId);
      await fetchHouseholdData();
    } catch (revokeError: any) {
      setHouseholdError(revokeError.message || 'Failed to revoke invite');
    }
  };

  const handleRemoveMember = async (memberUserId: string) => {
    if (!isPrimaryOwner || !homeId || memberUserId === session?.user?.id) return;
    try {
      await removeHomeMember(homeId, memberUserId);
      await fetchHouseholdData();
    } catch (removeError: any) {
      setHouseholdError(removeError.message || 'Failed to remove member');
    }
  };

  const handleTransferOwnership = async () => {
    if (!isPrimaryOwner || !homeId || !selectedTransferUserId) return;
    const targetMember = members.find((member) => member.user_id === selectedTransferUserId);
    if (!targetMember) return;

    const submitTransfer = async () => {
      try {
        setTransferringOwner(true);
        setHouseholdError('');
        await transferHomeOwnership(homeId, selectedTransferUserId);
        setSelectedTransferUserId(null);
        await fetchHouseholdData();
      } catch (transferError: any) {
        setHouseholdError(transferError.message || 'Failed to transfer ownership');
      } finally {
        setTransferringOwner(false);
      }
    };

    Alert.alert(
      'Transfer primary ownership?',
      `This will transfer primary ownership to ${targetMember.profile_name}. You will become a co-owner.`,
      [
        { text: 'Cancel', style: 'cancel' },
        { text: 'Transfer', style: 'destructive', onPress: submitTransfer },
      ],
    );
  };

  const handleSignOut = async () => {
    setLoading(true);
    try {
      await supabase.auth.signOut();
      navigate('/login', { replace: true });
    } catch (error) {
      console.error('Error during sign out:', error);
      navigate('/login', { replace: true });
    } finally {
      setLoading(false);
    }
  };

  const handleEmailUpdate = async () => {
    const nextEmail = emailInput.trim().toLowerCase();

    if (!nextEmail) {
      setEmailError('Email is required.');
      setEmailSuccess('');
      return;
    }

    if (nextEmail === (session?.user?.email || '').toLowerCase()) {
      setEmailError('That email is already on your account.');
      setEmailSuccess('');
      return;
    }

    setEmailSaving(true);
    setEmailError('');
    setEmailSuccess('');

    try {
      const { error: updateError } = await supabase.auth.updateUser({
        email: nextEmail,
      });

      if (updateError) throw updateError;

      setEmailSuccess('Email update requested. Check your inbox to confirm the change.');
    } catch (updateErr: any) {
      setEmailError(updateErr.message || 'Failed to update email.');
    } finally {
      setEmailSaving(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatCurrency = (amountCents: number, currency = 'usd') =>
    new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency.toUpperCase(),
      maximumFractionDigits: amountCents % 100 === 0 ? 0 : 2,
    }).format(amountCents / 100);

  const getProductPriceLabel = (product: BillingProduct) => {
    const displayPrice = product.metadata?.display_price;
    if (typeof displayPrice === 'string' && displayPrice.trim()) {
      return displayPrice;
    }
    if (product.amount_cents === 0) {
      return product.billing_mode === 'subscription' ? 'Configured in Stripe' : 'Free';
    }
    const interval = product.interval ? `/${product.interval}` : '';
    return `${formatCurrency(product.amount_cents, product.currency)}${interval}`;
  };

  const getProductCtaLabel = (product: BillingProduct) => {
    const cta = product.metadata?.cta;
    return typeof cta === 'string' && cta.trim() ? cta : 'Select';
  };

  const formatHomeLabel = (home: { address: string; city?: string; state?: string }) => {
    const location = [home.city, home.state].filter(Boolean).join(', ');
    return location ? `${home.address}, ${location}` : home.address;
  };

  const handleSelectBillingHome = (homeId: string) => {
    setSelectedBillingHomeId(homeId);
    setBillingError('');
    setBillingSuccess('');
    if (homes.length > 1) {
      setActiveHomeById(homeId);
    }
  };

  const visibleBillingProducts = (billingOverview?.products || []).filter((product) => {
    // Homeowner profile focuses on base plan selection for now; add-ons are admin-only.
    if (product.kind !== 'base_plan') {
      return false;
    }
    if (product.metadata?.admin_only === true) {
      return false;
    }
    if (product.plan_value === billingOverview?.current_plan.value) {
      return false;
    }
    return true;
  });

  const transferableMembers = members.filter(
    (member) => member.user_id !== session?.user?.id && member.role !== 'owner',
  );

  if (loading && !profile) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color="#9ca3af" />
      </View>
    );
  }

  return (
    <WebScrollView 
      style={styles.scrollView}
      contentContainerStyle={styles.container}
      showsVerticalScrollIndicator={false}
    >
      <View style={styles.header}>
        <Text style={styles.title}>Profile</Text>
        <Text style={styles.subtitle}>Manage your account information</Text>
      </View>

      <View style={styles.content}>
        <View style={styles.card}>
          <Text style={styles.cardTitle}>Personal Information</Text>
          <View style={styles.form}>
            <View style={styles.inputContainer}>
              <Text style={styles.label}>Full Name</Text>
              <TextInput
                style={styles.input}
                value={formData.fullName}
                onChangeText={(value) => setFormData({ ...formData, fullName: value })}
                placeholder="Enter your full name"
                placeholderTextColor="#9ca3af"
              />
            </View>
            
            <View style={styles.inputContainer}>
              <Text style={styles.label}>Email</Text>
              <TextInput
                style={styles.input}
                value={emailInput}
                onChangeText={setEmailInput}
                placeholder="Enter your email"
                placeholderTextColor="#9ca3af"
                autoCapitalize="none"
                keyboardType="email-address"
              />
              <Pressable
                onPress={handleEmailUpdate}
                disabled={emailSaving}
                style={({ pressed }) => [
                  styles.secondaryButton,
                  (emailSaving || pressed) && styles.saveButtonDisabled
                ]}
              >
                <Text style={styles.secondaryButtonText}>
                  {emailSaving ? 'Updating...' : 'Update Email'}
                </Text>
              </Pressable>
              {emailError ? <Text style={styles.errorText}>{emailError}</Text> : null}
              {emailSuccess ? <Text style={styles.successText}>{emailSuccess}</Text> : null}
            </View>
            
            <View style={styles.inputContainer}>
              <Text style={styles.label}>Phone Number</Text>
              <TextInput
                style={styles.input}
                value={formData.phoneNumber}
                onChangeText={(value) => setFormData({ ...formData, phoneNumber: value })}
                placeholder="Enter your phone number"
                placeholderTextColor="#9ca3af"
                keyboardType="phone-pad"
              />
            </View>

            {error && (
              <Text style={styles.errorText}>{error}</Text>
            )}

            <Pressable
              onPress={handleSubmit}
              disabled={saving}
              style={({ pressed }) => [
                styles.saveButton,
                (saving || pressed) && styles.saveButtonDisabled
              ]}
            >
              {saving ? (
                <View style={styles.buttonContent}>
                  <ActivityIndicator size="small" color="#ffffff" />
                  <Text style={styles.buttonText}>Saving...</Text>
                </View>
              ) : (
                <Text style={styles.buttonText}>Save Changes</Text>
              )}
            </Pressable>
          </View>
        </View>

        <View style={styles.card}>
          <Text style={styles.cardTitle}>Account Summary</Text>
          <View style={styles.infoSection}>
            <View style={styles.infoRow}>
              <User size={20} color="#9ca3af" />
              <Text style={styles.infoText}>
                Member since {profile?.created_at ? formatDate(profile.created_at) : 'N/A'}
              </Text>
            </View>
            <View style={styles.infoRow}>
              <HomeIcon size={20} color="#9ca3af" />
              <Text style={styles.infoText}>
                {homes.length === 1 ? '1 Home' : `${homes.length} Homes`}
              </Text>
            </View>
          </View>
        </View>

        <View style={styles.card}>
          <View style={styles.cardTitleRow}>
            <Text style={styles.cardTitleNoMargin}>Billing & Subscription</Text>
            {billingLoading ? <ActivityIndicator size="small" color="#6b7280" /> : null}
          </View>

          {billingError ? <Text style={styles.errorText}>{billingError}</Text> : null}
          {billingSuccess ? <Text style={styles.successText}>{billingSuccess}</Text> : null}

          {homes.length === 0 ? (
            <Text style={styles.infoText}>Add a home before managing billing.</Text>
          ) : billingOverview ? (
            <View style={styles.billingSection}>
              {homes.length > 1 ? (
                <View style={styles.billingHomeSelector}>
                  <Text style={styles.billingLabel}>Billing home</Text>
                  <Text style={styles.infoTextSmall}>
                    Choose which home you want to view plans and checkout for. Markets and billing
                    permissions can differ by home.
                  </Text>
                  <View style={styles.billingHomeOptions}>
                    {homes.map((home) => {
                      const isSelected = selectedBillingHomeId === home.id;
                      const isOwner = home.owner_id === session?.user?.id;
                      return (
                        <Pressable
                          key={home.id}
                          onPress={() => handleSelectBillingHome(home.id)}
                          style={[
                            styles.billingHomeOption,
                            isSelected && styles.billingHomeOptionSelected,
                          ]}
                        >
                          <View style={styles.billingHomeOptionContent}>
                            <Text
                              style={[
                                styles.billingHomeOptionTitle,
                                isSelected && styles.billingHomeOptionTitleSelected,
                              ]}
                            >
                              {home.address}
                            </Text>
                            <Text
                              style={[
                                styles.billingHomeOptionMeta,
                                isSelected && styles.billingHomeOptionMetaSelected,
                              ]}
                            >
                              {[home.city, home.state].filter(Boolean).join(', ')}
                              {isOwner ? '' : ' • View only'}
                            </Text>
                          </View>
                        </Pressable>
                      );
                    })}
                  </View>
                </View>
              ) : (
                <Text style={styles.infoText}>
                  Billing for {formatHomeLabel(homes[0])}.
                </Text>
              )}

              <View style={styles.billingSummary}>
                <View>
                  <Text style={styles.billingLabel}>Current plan</Text>
                  <Text style={styles.billingPlanName}>{billingOverview.current_plan.label}</Text>
                  {billingOverview.current_plan.subscription ? (
                    <Text style={styles.infoTextSmall}>
                      Stripe status: {billingOverview.current_plan.subscription.status}
                      {billingOverview.current_plan.subscription.current_period_end
                        ? ` • renews ${formatDate(billingOverview.current_plan.subscription.current_period_end)}`
                        : ''}
                    </Text>
                  ) : (
                    <Text style={styles.infoTextSmall}>Based on this home's HoneyDone plan.</Text>
                  )}
                </View>
                <View style={styles.planBadge}>
                  <Text style={styles.planBadgeText}>
                    {billingOverview.current_plan.source === 'stripe'
                      ? 'Stripe synced'
                      : billingOverview.current_plan.source === 'complimentary'
                        ? 'Complimentary'
                        : billingOverview.current_plan.source === 'manual'
                          ? 'Manual'
                          : 'Home plan'}
                  </Text>
                </View>
              </View>

              {billingOverview.home ? (
                <Text style={styles.infoText}>
                  Plans and upgrades for {billingOverview.home.market_name || 'your market'}
                  {billingOverview.home.address ? ` at ${billingOverview.home.address}` : ''}.
                </Text>
              ) : null}

              {!billingOverview.checkout_allowed && billingOverview.home ? (
                <Text style={styles.legalSmallText}>
                  {billingOverview.current_plan.subscription
                    ? 'This home already has an active subscription.'
                    : 'Only the primary owner can manage billing for this home.'}
                </Text>
              ) : null}

              {visibleBillingProducts.length === 0 ? (
                <View style={styles.emptyBillingBox}>
                  <Text style={styles.infoTextBold}>No plan changes available right now.</Text>
                  <Text style={styles.legalSmallText}>
                    You are on the highest plan available for your account, or plan upgrades are not
                    yet configured for your market.
                  </Text>
                </View>
              ) : (
                <View style={styles.productGrid}>
                  {visibleBillingProducts.map((product) => {
                    const isCheckingOut = checkoutProductId === product.id;
                    const checkoutDisabled =
                      !billingOverview.checkout_allowed || !product.stripe_price_id || isCheckingOut;
                    return (
                      <View key={product.id} style={styles.productCard}>
                        <View style={styles.productHeader}>
                          <View style={styles.productKindBadge}>
                            <Text style={styles.productKindText}>
                              {product.kind === 'base_plan' ? 'Plan' : 'Add-on'}
                            </Text>
                          </View>
                          <Text style={styles.productPrice}>{getProductPriceLabel(product)}</Text>
                        </View>
                        <Text style={styles.productTitle}>{product.name}</Text>
                        {product.description ? (
                          <Text style={styles.productDescription}>{product.description}</Text>
                        ) : null}
                        {product.included_units ? (
                          <Text style={styles.productMeta}>
                            Includes {product.included_units} {product.included_units === 1 ? 'unit' : 'units'}
                            {product.additional_unit_amount_cents
                              ? ` • ${formatCurrency(product.additional_unit_amount_cents, product.currency)}/hr after`
                              : ''}
                          </Text>
                        ) : null}
                        <Pressable
                          onPress={() => handleStartCheckout(product)}
                          disabled={checkoutDisabled}
                          style={({ pressed }) => [
                            styles.productButton,
                            checkoutDisabled && styles.saveButtonDisabled,
                            pressed && styles.productButtonPressed,
                          ]}
                        >
                          {isCheckingOut ? (
                            <ActivityIndicator size="small" color="#ffffff" />
                          ) : (
                            <Text style={styles.buttonText}>
                              {product.stripe_price_id
                                ? getProductCtaLabel(product)
                                : 'Coming soon'}
                            </Text>
                          )}
                        </Pressable>
                      </View>
                    );
                  })}
                </View>
              )}
            </View>
          ) : billingLoading ? (
            <View style={styles.buttonContent}>
              <ActivityIndicator size="small" color="#6b7280" />
              <Text style={styles.infoText}>Loading billing information...</Text>
            </View>
          ) : (
            <Text style={styles.infoText}>Billing information is unavailable right now.</Text>
          )}
        </View>

        <View style={styles.card}>
          <Text style={styles.cardTitle}>Household</Text>
          {acceptingInvite && (
            <Text style={styles.legalSmallText}>Accepting household invite...</Text>
          )}
          {householdLoading ? (
            <ActivityIndicator size="small" color="#6b7280" />
          ) : (
            <View style={styles.householdSection}>
              <HomePicker label="Household home" compact />
              {homes.length <= 1 ? (
                <Text style={styles.infoText}>
                  {homeAddress ? `Shared home: ${homeAddress}` : 'No household membership found'}
                </Text>
              ) : !homeAddress ? (
                <Text style={styles.infoText}>No household membership found</Text>
              ) : null}
              {!isPrimaryOwner && homeAddress ? (
                <Text style={styles.legalSmallText}>
                  Only the primary owner can manage invites, billing, and ownership transfer.
                </Text>
              ) : null}

              {homeAddress && isPrimaryOwner ? (
                <View style={styles.householdInviteBox}>
                  <Text style={styles.label}>Invite household user</Text>
                  <TextInput
                    style={styles.input}
                    value={inviteEmail}
                    onChangeText={setInviteEmail}
                    placeholder="email@example.com"
                    autoCapitalize="none"
                    keyboardType="email-address"
                  />
                  <View style={styles.roleRow}>
                    <Pressable
                      onPress={() => setInviteRole('co_owner')}
                      style={[styles.rolePill, inviteRole === 'co_owner' && styles.rolePillActive]}
                    >
                      <Text style={[styles.rolePillText, inviteRole === 'co_owner' && styles.rolePillTextActive]}>
                        Co-owner
                      </Text>
                    </Pressable>
                    <Pressable
                      onPress={() => setInviteRole('member')}
                      style={[styles.rolePill, inviteRole === 'member' && styles.rolePillActive]}
                    >
                      <Text style={[styles.rolePillText, inviteRole === 'member' && styles.rolePillTextActive]}>
                        Member
                      </Text>
                    </Pressable>
                  </View>
                  <Pressable
                    onPress={handleSendInvite}
                    disabled={inviteSubmitting || !inviteEmail.trim()}
                    style={({ pressed }) => [
                      styles.saveButton,
                      (inviteSubmitting || !inviteEmail.trim() || pressed) && styles.saveButtonDisabled,
                    ]}
                  >
                    <Text style={styles.buttonText}>
                      {inviteSubmitting ? 'Sending...' : 'Send Invite'}
                    </Text>
                  </Pressable>
                </View>
              ) : null}

              <View style={styles.householdList}>
                <Text style={styles.label}>Members</Text>
                {members.map((member) => (
                  <View key={member.id} style={styles.householdRow}>
                    <Text style={styles.infoTextBold}>
                      {member.profile_name} ({member.role.replace('_', ' ')})
                    </Text>
                    {isPrimaryOwner && member.user_id !== session?.user?.id && member.role !== 'owner' ? (
                      <Pressable onPress={() => handleRemoveMember(member.user_id)}>
                        <Text style={styles.removeText}>Remove</Text>
                      </Pressable>
                    ) : null}
                  </View>
                ))}
              </View>

              {isPrimaryOwner ? (
                <View style={styles.householdList}>
                  <Text style={styles.label}>Pending invites</Text>
                  {invitations.length === 0 ? (
                    <Text style={styles.legalSmallText}>No pending invites</Text>
                  ) : (
                    invitations.map((invitation) => (
                      <View key={invitation.id} style={styles.householdRow}>
                        <View>
                          <Text style={styles.infoTextBold}>
                            {invitation.email} ({invitation.role.replace('_', ' ')})
                          </Text>
                          <Text style={styles.legalSmallText}>
                            Expires {formatDate(invitation.expires_at)}
                          </Text>
                        </View>
                        <Pressable onPress={() => handleRevokeInvite(invitation.id)}>
                          <Text style={styles.removeText}>Revoke</Text>
                        </Pressable>
                      </View>
                    ))
                  )}
                </View>
              ) : null}

              {isPrimaryOwner ? (
                <View style={styles.householdInviteBox}>
                  <Text style={styles.label}>Transfer primary ownership</Text>
                  <Text style={styles.legalSmallText}>
                    Billing, invite management, and destructive home actions move to the new owner.
                  </Text>
                  <View style={styles.roleRow}>
                    {transferableMembers.length === 0 ? (
                      <Text style={styles.legalSmallText}>Invite a co-owner/member before transferring ownership.</Text>
                    ) : (
                      transferableMembers.map((member) => (
                        <Pressable
                          key={member.id}
                          onPress={() => setSelectedTransferUserId(member.user_id)}
                          style={[
                            styles.rolePill,
                            selectedTransferUserId === member.user_id && styles.rolePillActive,
                          ]}
                        >
                          <Text
                            style={[
                              styles.rolePillText,
                              selectedTransferUserId === member.user_id && styles.rolePillTextActive,
                            ]}
                          >
                            {member.profile_name}
                          </Text>
                        </Pressable>
                      ))
                    )}
                  </View>
                  <Pressable
                    onPress={handleTransferOwnership}
                    disabled={!selectedTransferUserId || transferringOwner}
                    style={({ pressed }) => [
                      styles.transferButton,
                      (!selectedTransferUserId || transferringOwner || pressed) && styles.saveButtonDisabled,
                    ]}
                  >
                    <Text style={styles.buttonText}>
                      {transferringOwner ? 'Transferring...' : 'Transfer ownership'}
                    </Text>
                  </Pressable>
                </View>
              ) : null}
            </View>
          )}
          {householdError ? <Text style={styles.errorText}>{householdError}</Text> : null}
        </View>

        <View style={styles.card}>
          <Text style={styles.cardTitle}>Legal</Text>
          <View style={styles.legalSection}>
            <Pressable
              onPress={() => {
                if (Platform.OS === 'web') {
                  window.open('/terms-of-service', '_blank');
                } else {
                  navigate('/terms-of-service');
                }
              }}
              style={({ pressed }) => [
                styles.linkButton,
                pressed && styles.linkButtonPressed
              ]}
            >
              <Text style={styles.linkText}>Terms of Service</Text>
            </Pressable>
            
            <Pressable
              onPress={() => {
                if (Platform.OS === 'web') {
                  window.open('/privacy-policy', '_blank');
                } else {
                  navigate('/privacy-policy');
                }
              }}
              style={({ pressed }) => [
                styles.linkButton,
                pressed && styles.linkButtonPressed
              ]}
            >
              <Text style={styles.linkText}>Privacy Policy</Text>
            </Pressable>
            
            {profile?.terms_accepted_at && (
              <Text style={styles.legalSmallText}>
                Terms accepted on {formatDate(profile.terms_accepted_at)}
              </Text>
            )}
          </View>
        </View>

        <View style={styles.card}>
          <Text style={styles.cardTitle}>Account Actions</Text>
          <Pressable
            onPress={handleSignOut}
            disabled={loading}
            style={({ pressed }) => [
              styles.signOutButton,
              (loading || pressed) && styles.signOutButtonDisabled
            ]}
          >
            {loading ? (
              <View style={styles.buttonContent}>
                <ActivityIndicator size="small" color="#dc2626" />
                <Text style={styles.signOutButtonText}>Signing out...</Text>
              </View>
            ) : (
              <View style={styles.buttonContent}>
                <LogOut size={16} color="#dc2626" />
                <Text style={styles.signOutButtonText}>Sign out</Text>
              </View>
            )}
          </Pressable>
        </View>
      </View>
    </WebScrollView>
  );
};

const styles = StyleSheet.create({
  loadingContainer: {
    minHeight: 400,
    justifyContent: 'center',
    alignItems: 'center',
  },
  scrollView: {
    flex: 1,
  },
  container: {
    gap: 32,
    paddingBottom: 16,
  },
  header: {
    marginBottom: 8,
  },
  title: {
    fontSize: 30,
    fontWeight: 'bold',
    color: '#111827',
  },
  subtitle: {
    fontSize: 16,
    color: '#4b5563',
    marginTop: 4,
  },
  content: {
    gap: 24,
  },
  card: {
    backgroundColor: '#ffffff',
    padding: 24,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#e5e7eb',
    ...shadows.sm,
  },
  cardTitle: {
    fontSize: 20,
    fontWeight: '600',
    color: '#111827',
    marginBottom: 24,
  },
  cardTitleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    marginBottom: 20,
  },
  cardTitleNoMargin: {
    fontSize: 20,
    fontWeight: '600',
    color: '#111827',
  },
  form: {
    gap: 16,
  },
  inputContainer: {
    marginBottom: 4,
  },
  label: {
    fontSize: 14,
    fontWeight: '500',
    color: '#374151',
    marginBottom: 4,
  },
  input: {
    width: '100%',
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderWidth: 1,
    borderColor: '#d1d5db',
    borderRadius: 6,
    fontSize: 16,
    backgroundColor: '#ffffff',
    color: '#111827',
    ...(Platform.OS === 'web' && {
      outlineStyle: 'none' as any,
    }),
  },
  errorText: {
    color: '#ef4444',
    fontSize: 14,
  },
  successText: {
    color: '#166534',
    fontSize: 14,
  },
  saveButton: {
    width: '100%',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 8,
    paddingHorizontal: 16,
    backgroundColor: '#000000',
    borderRadius: 6,
    marginTop: 8,
  },
  saveButtonDisabled: {
    opacity: 0.5,
  },
  secondaryButton: {
    alignSelf: 'flex-start',
    marginTop: 8,
    paddingVertical: 6,
    paddingHorizontal: 10,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: '#d1d5db',
    backgroundColor: '#f9fafb',
  },
  secondaryButtonText: {
    color: '#111827',
    fontSize: 14,
    fontWeight: '500',
  },
  buttonContent: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  buttonText: {
    color: '#ffffff',
    fontSize: 16,
    fontWeight: '500',
  },
  infoSection: {
    gap: 16,
  },
  infoRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  infoText: {
    fontSize: 14,
    color: '#4b5563',
    flex: 1,
  },
  infoTextBold: {
    fontSize: 14,
    fontWeight: '500',
    color: '#111827',
  },
  infoTextSmall: {
    fontSize: 12,
    color: '#6b7280',
    marginTop: 2,
  },
  billingSection: {
    gap: 16,
  },
  billingHomeSelector: {
    gap: 8,
  },
  billingHomeOptions: {
    gap: 8,
  },
  billingHomeOption: {
    width: '100%',
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 14,
    paddingHorizontal: 14,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: '#d1d5db',
    backgroundColor: '#ffffff',
    ...(Platform.OS === 'web' && {
      cursor: 'pointer',
    }),
  },
  billingHomeOptionContent: {
    flex: 1,
    gap: 2,
  },
  billingHomeOptionSelected: {
    borderColor: '#111827',
    backgroundColor: '#f9fafb',
  },
  billingHomeOptionTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: '#111827',
  },
  billingHomeOptionTitleSelected: {
    color: '#111827',
  },
  billingHomeOptionMeta: {
    fontSize: 12,
    color: '#6b7280',
  },
  billingHomeOptionMetaSelected: {
    color: '#4b5563',
  },
  billingSummary: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 12,
    padding: 14,
    borderWidth: 1,
    borderColor: '#e5e7eb',
    borderRadius: 10,
    backgroundColor: '#f9fafb',
  },
  billingLabel: {
    fontSize: 12,
    fontWeight: '600',
    color: '#6b7280',
    textTransform: 'uppercase',
    letterSpacing: 0.4,
  },
  billingPlanName: {
    fontSize: 22,
    fontWeight: '700',
    color: '#111827',
    marginTop: 2,
  },
  planBadge: {
    borderRadius: 999,
    paddingHorizontal: 10,
    paddingVertical: 5,
    backgroundColor: '#eef2ff',
  },
  planBadgeText: {
    color: '#3730a3',
    fontSize: 12,
    fontWeight: '700',
  },
  emptyBillingBox: {
    gap: 4,
    padding: 12,
    borderRadius: 8,
    backgroundColor: '#f9fafb',
    borderWidth: 1,
    borderColor: '#e5e7eb',
  },
  productGrid: {
    gap: 12,
  },
  productCard: {
    gap: 10,
    padding: 14,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: '#e5e7eb',
    backgroundColor: '#ffffff',
  },
  productHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
  },
  productKindBadge: {
    borderRadius: 999,
    paddingHorizontal: 8,
    paddingVertical: 4,
    backgroundColor: '#f3f4f6',
  },
  productKindText: {
    color: '#374151',
    fontSize: 12,
    fontWeight: '700',
  },
  productPrice: {
    color: '#111827',
    fontSize: 14,
    fontWeight: '700',
  },
  productTitle: {
    fontSize: 16,
    fontWeight: '700',
    color: '#111827',
  },
  productDescription: {
    fontSize: 14,
    color: '#4b5563',
    lineHeight: 20,
  },
  productMeta: {
    fontSize: 12,
    color: '#6b7280',
    fontWeight: '600',
  },
  productButton: {
    width: '100%',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 8,
    paddingHorizontal: 16,
    backgroundColor: '#111827',
    borderRadius: 6,
    marginTop: 2,
  },
  productButtonPressed: {
    opacity: 0.85,
  },
  legalSection: {
    gap: 16,
  },
  linkButton: {
    paddingVertical: 4,
  },
  linkButtonPressed: {
    opacity: 0.6,
  },
  linkText: {
    fontSize: 14,
    color: '#4b5563',
    ...(Platform.OS === 'web' && {
      cursor: 'pointer' as any,
    }),
  },
  legalSmallText: {
    fontSize: 12,
    color: '#9ca3af',
    marginTop: 4,
  },
  signOutButton: {
    width: '100%',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 8,
    paddingHorizontal: 16,
    borderWidth: 1,
    borderColor: '#dc2626',
    borderRadius: 6,
    backgroundColor: '#ffffff',
  },
  signOutButtonDisabled: {
    opacity: 0.5,
  },
  signOutButtonText: {
    color: '#dc2626',
    fontSize: 16,
    fontWeight: '500',
  },
  householdSection: {
    gap: 16,
  },
  householdInviteBox: {
    gap: 10,
    padding: 12,
    borderWidth: 1,
    borderColor: '#e5e7eb',
    borderRadius: 8,
    backgroundColor: '#f9fafb',
  },
  roleRow: {
    flexDirection: 'row',
    gap: 8,
  },
  rolePill: {
    borderWidth: 1,
    borderColor: '#d1d5db',
    borderRadius: 999,
    paddingHorizontal: 10,
    paddingVertical: 6,
  },
  rolePillActive: {
    borderColor: '#111827',
    backgroundColor: '#111827',
  },
  rolePillText: {
    fontSize: 12,
    color: '#4b5563',
    fontWeight: '500',
  },
  rolePillTextActive: {
    color: '#ffffff',
  },
  householdList: {
    gap: 8,
  },
  householdRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    paddingVertical: 6,
    borderBottomWidth: 1,
    borderBottomColor: '#f3f4f6',
  },
  removeText: {
    color: '#dc2626',
    fontSize: 13,
    fontWeight: '500',
  },
  transferButton: {
    width: '100%',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 8,
    paddingHorizontal: 16,
    backgroundColor: '#dc2626',
    borderRadius: 6,
    marginTop: 6,
  },
});

export default Profile;